<template>
  <div class="fts-design-element">
    <div class="element-designer-item-action-container" v-if="!preview">
      <fts-form-design-shortcut
        :modules="modules"
        :options="shortcutOptions"
        :elements="shortcutElements"
        :selected-options="shortcutSelectedOptions"
        suggestion
        placement="bottom-start"
        @option:selected="$emit('shortcut-option:selected', $event)"
        @option:create="$emit('shortcut-option:create', $event)"
        class="element-designer-item-action"
      >
        <fts-tippy>
          <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
          <div slot="content" class="tippy-hint">
            <span class="tippy-hint-highlight">Click</span> to add a field below
          </div>
        </fts-tippy>
      </fts-form-design-shortcut>
      <fts-dropdown-select
        append-to-body
        append-content-class="dropdown-menu"
        class="element-designer-item-action element-designer-item-action--drag-handle"
        @selected="(evt) => selectMenuAction(evt, item)"
      >
        <fts-tippy slot="toggle">
          <font-awesome-icon
            :icon="['fas', 'grip-vertical']"
            fixed-width
          />
          <template #content>
            <div class="tippy-hint">
              <span class="tippy-hint-highlight">Drag</span> to move
            </div>
            <div class="tippy-hint">
              <span class="tippy-hint-highlight">Click</span> to open menu
            </div>
          </template>
        </fts-tippy>
        <template #menu="{ select }">
          <div
            class="dropdown-option"
            @click="select('configure')"
            v-if="
              item.input !== 'empty' &&
              item.input !== 'section' &&
              item.input !== 'description' &&
              item.input !== 'image' &&
              !document
            "
          >
            <div class="dropdown-option-icon">
              <font-awesome-icon :icon="['fas', 'cog']" fixed-width />
            </div>
            <div>Configure</div>
          </div>
          <div class="dropdown-option" @click="select('remove')">
            <div class="dropdown-option-icon">
              <font-awesome-icon :icon="['fas', 'times']" fixed-width />
            </div>
            <div>Remove</div>
          </div>
          <template v-if="
            item.input !== 'empty' &&
            item.input !== 'section' &&
            item.input !== 'description' &&
            item.input !== 'image' &&
            item.input !== 'button' &&
            item.input !== 'custom'
          ">
            <div class="dropdown-divider"></div>
            <div class="dropdown-option" @click="select('edit')">
              <div class="dropdown-option-icon">
                <font-awesome-icon :icon="['far', 'edit']" fixed-width />
              </div>
              <div>Edit Information</div>
            </div>
            <div class="dropdown-option dropdown-option--danger" @click="select('delete')">
              <div class="dropdown-option-icon">
                <font-awesome-icon :icon="['far', 'trash-alt']" fixed-width />
              </div>
              <div>Delete Information</div>
            </div>
          </template>
        </template>
      </fts-dropdown-select>
    </div>
    <div class="design-element-wrapper">
      <!-- custom:description -->
      <div
        class="design-element-custom-description"
        v-if="item.input == 'description'"
      >
        <fts-form-editor :content.sync="item.content" :el-parent-id="elParentId" />
      </div>
      <!-- custom:image -->
      <div
        class="design-element-custom-image"
        v-else-if="item.input == 'image'"
      >
        <fts-file-upload
          :vectorId="vectorId"
          :item="item.item"
          :formData="item"
        />
      </div>
      <!-- empty space: pure reserved space — the kind bar below names it -->
      <div
        class="design-element-empty-space"
        v-else-if="item.input == 'empty'"
      ></div>
      <!-- custom:button -->
      <div
        class="design-element-custom-button"
        v-else-if="item.input == 'button'"
      >
        <fts-btn
          outline
          :color="'primary'"
          text-transform-init
        >
          {{item.label ? item.label : "(unnamed)"}}
        </fts-btn>
      </div>
      <!-- section -->
      <template v-else-if="item.input === 'section'">
        <template v-if="item.type === 'tab'">
          <div
            v-for="(tab, tabIndex) in getTabs(item.tabs)"
            :key="tab.id"
            class="design-element-section design-element-section--tab"
          >
            <div class="design-element-section__header">
              <div class="design-element-section__header-l">
                <div class="design-element-section__header-tag">
                  {{ item.type }}
                </div>
                <fts-contenteditable
                  class="design-element-section__header-title"
                  v-model="tab.header"
                />
              </div>
              <div class="design-element-section__header-r">
                <div class="design-element-section__header-btn-wrapper" v-if="!preview">
                  <fts-btn
                    text
                    icon
                    sm
                    @click="removeTab(tab)"
                    v-if="getTabs(item.tabs).length > 1"
                  >
                    <font-awesome-icon :icon="['far', 'trash-alt']" />
                  </fts-btn>
                  <fts-btn
                    text
                    icon
                    sm
                    @click="addTab(tabIndex + 1)"
                  >
                    <font-awesome-icon :icon="['fas', 'plus']" />
                  </fts-btn>
                </div>
              </div>
            </div>
            <div class="design-element-section__content">
              <fts-form-layouter
                :modules="modules"
                :form="tab.design"
                :form-data="formData"
                :activity-id="activityId"
                :activity-design="form"
                :item-group="'row'"
                :shortcut-options="shortcutOptions"
                :shortcut-elements="shortcutElements"
                :shortcut-selected-options="shortcutSelectedOptions"
                :document="document"
                :preview="preview"
                :preview-data="previewData"
                @added="elementAdded"
              />
            </div>
          </div>
        </template>
        <div class="design-element-section" v-else>
          <div class="design-element-section__header">
            <div class="design-element-section__header-l">
              <div class="design-element-section__header-tag">
                {{ item.type }}
              </div>
              <fts-contenteditable
                class="design-element-section__header-title"
                v-model="item.header"
              />
            </div>
            <div class="design-element-section__header-r">
              <fts-toggle-switch
                :form-data="item"
                :item="{ fieldName: 'expanded' }"
                :size="18"
                :label-dir="'left'"
                :label-text="'Expanded by Default'"
                v-if="item.type === 'collapse' && !preview"
              />
            </div>
          </div>
          <div class="design-element-section__content">
            <fts-form-layouter
              :modules="modules"
              :form="item.design"
              :form-data="formData"
              :activity-id="activityId"
              :activity-design="form"
              :item-group="'row'"
              :shortcut-options="shortcutOptions"
              :shortcut-elements="shortcutElements"
              :shortcut-selected-options="shortcutSelectedOptions"
              :document="document"
              @added="elementAdded"
            />
          </div>
        </div>
      </template>
      <!-- design element default -->
      <div class="design-element__default" v-else>
        <!-- per-field sync indicator (design-time twin of the runtime trigger model): does
             editing this field round-trip, and which origin — formula / automation / script.
             Plus the computed-field notice: stored value only when its source fields are not
             on this form (no local recompute). -->
        <fts-sync-chip
          :el="item"
          :disabled="Boolean(document)"
          :stored="storedOnlyBadgeFor(item)"
          v-if="!preview"
        />
        <!-- PREVIEW: the real control, interactive on the shared scratch entry (no overlay class —
             design-element-form-element carries the inert "Preview" veil) -->
        <template v-if="preview">
          <fts-form-element
            :form-data="previewData || {}"
            :vector-id="vectorId && vectorId.toString()"
            :activity-id="previewActivityId"
            :item="{
              ...item,
              required: Boolean(item.required || item.requiredFormula) && !item.readOnly && !item.formulaMode
            }"
            class="design-element-form-preview"
            v-if="
              item.type !== 14 &&
              item.type !== 15 &&
              item.input !== 'table' &&
              item.input !== 'list' &&
              item.visible !== false
            "
          />
        </template>
        <!-- DOCUMENT: the legacy inert preview -->
        <template v-else-if="document">
          <template v-if="
            item.type !== 14 &&
            item.type !== 15 &&
            item.input !== 'table' &&
            item.input !== 'list'
          ">
            <fts-form-element
              :form-data="{}"
              :item="{
                ...item,
                required: Boolean(item.required || item.requiredFormula) && !item.readOnly && !item.formulaMode
              }"
              class="design-element-form-element"
              :class="[
                { 'design-element-form-element-signature': item.type === 29 },
                { 'design-element-form-element--invisible': item.visible === false },
                { 'design-element-form-element--readonly': item.readOnly }
              ]"
            />
            <div class="design-element-form-overlay"></div>
          </template>
          <fts-form-label
            :icon="getIcon(item.type)"
            icon-right
            placement="top"
            :required="Boolean(item.required || item.requiredFormula) && !item.readOnly && !item.formulaMode"
            class="design-element-form-label"
            :class="{ 'design-element-form-label--invisible': item.visible === false}"
            v-else
          >
            {{ item.label || item.displayName }}
          </fts-form-label>
        </template>
        <!-- DESIGN (rest): the compact ledger row — label + one quiet meta line; click to open
             the same behavior ledger the formula modal hosts (§3.2b: the row is the disclosure) -->
        <template v-else>
          <div
            class="design-element-ledger-head"
            :class="{ 'design-element-ledger-head--open': expanded }"
            @click="expanded = !expanded"
          >
            <fts-form-label
              :icon="getIcon(item.type)"
              placement="top"
              class="design-element-form-label"
              :class="{ 'design-element-form-label--invisible': item.visible === false}"
            >
              {{ item.label || item.displayName }}
              <!-- config truth, not the runtime gate: the designer marks required whenever the
                   rule is set, even on view-only / formula-mode fields -->
              <i
                class="fts-icon-asterisk design-element-required-mark"
                v-if="item.required || item.requiredFormula"
              ></i>
            </fts-form-label>
            <div class="design-element-meta" v-if="metaLines.length">
              <!-- one separately-ellipsized line per formula fact; rule lines carry their icon,
                   the value formula leads with just "="; short facts share the last line -->
              <div class="design-element-meta-line" v-for="(line, li) in metaLines" :key="'ml' + li">
                <span class="design-element-meta-err" :title="line.err" v-if="line.err">✕</span>
                <span class="design-element-meta-fact" v-for="(fact, fi) in line.facts" :key="'mf' + li + '-' + fi">
                  <span class="design-element-meta-eq" v-if="fact.eq">=</span>
                  <i
                    :class="[fact.icon, 'design-element-meta-icon']"
                    :title="fact.title"
                    v-if="fact.icon"
                  ></i>
                  <b v-if="fact.strong">{{ fact.strong }}</b>
                  <template v-if="fact.text">{{ fact.text }}</template>
                  <fts-formula-display
                    :variables="variables"
                    :form="form"
                    :formula="fact.formula"
                    v-if="fact.formula"
                  />
                  <span class="design-element-meta-sep" v-if="fi < line.facts.length - 1">·</span>
                </span>
              </div>
            </div>
          </div>
          <div class="design-element-ledger-body" v-if="expanded">
            <fts-field-behavior-ledger
              :form-data="item"
              :variables="variables"
              :form="form"
              :actions="getActions(item)"
              :information-formula="computedFormula(item) || informationFormula(item)"
              :is-computed="isComputed(item)"
              :activity-id="activityId"
            >
              <template #extra-rules>
                <div class="formula-rule-group" v-for="row in extraToggles" :key="row.key">
                  <div class="formula-rule">
                    <div class="form-formula-label">
                      <i :class="[row.icon, 'form-formula-label-icon']"></i> {{ row.label }}
                    </div>
                    <div class="formula-rule-states">
                      <template v-for="(st, si) in row.states">
                        <span class="formula-rule-sep" v-if="si" :key="row.key + 'sep' + si">·</span>
                        <span
                          class="formula-rule-state"
                          :class="{ 'formula-rule-state--on': st.active }"
                          @click="!st.active && row.toggle()"
                          :key="row.key + 'st' + si"
                        >{{ st.label }}</span>
                      </template>
                    </div>
                  </div>
                </div>
              </template>
            </fts-field-behavior-ledger>
            <div class="design-element-ledger-actions">
              <template v-if="!document">
                <span class="design-element-ledger-action" @click="selectMenuAction('configure', item)">Configure</span>
                <span class="design-element-ledger-action-sep">·</span>
              </template>
              <template v-if="isInformationItem">
                <span class="design-element-ledger-action" @click="selectMenuAction('edit', item)">Edit information</span>
                <span class="design-element-ledger-action-sep">·</span>
              </template>
              <span class="design-element-ledger-action" @click="selectMenuAction('remove', item)">Remove from form</span>
              <span
                class="design-element-ledger-action design-element-ledger-action--danger"
                @click="selectMenuAction('delete', item)"
                v-if="isInformationItem"
              >Delete information</span>
            </div>
          </div>
        </template>
        <!-- information:table -->
        <template v-if="item.type == 14">
          <!-- design mode (ruling 2026-08-11, mockup F): columns as a nested vertical ledger —
               each column a full-width row with the field vocabulary (meta lines, inline
               behavior ledger, drag order = column order); hidden columns become one quiet
               line. The horizontal strip below remains the preview/document rendering. -->
          <div class="design-element-table-rows" v-if="!document && !preview">
            <draggable
              :group="item.fieldName"
              :list="item.fields"
              v-bind="dragOptions"
              handle=".design-element-table-row-grip"
              draggable=".design-element-table-row"
            >
              <template v-for="field in item.fields">
                <div class="design-element-table-row" :key="'tr' + field.id" v-if="!field.hide">
                  <span class="design-element-table-row-grip">
                    <font-awesome-icon :icon="['fas', 'grip-vertical']" />
                  </span>
                  <div class="design-element-table-row-main">
                    <div class="design-element-ledger-head" @click="toggleColumn(field)">
                      <fts-form-label
                        :icon="getIcon(field.type)"
                        placement="top"
                        class="design-element-form-label"
                        :class="{ 'design-element-form-label--invisible': field.visible === false }"
                      >
                        {{ field.label || field.displayName }}
                        <i
                          class="fts-icon-asterisk design-element-required-mark"
                          v-if="field.required || field.requiredFormula"
                        ></i>
                      </fts-form-label>
                      <div class="design-element-meta" v-if="metaLinesFor(field).length">
                        <div class="design-element-meta-line" v-for="(line, li) in metaLinesFor(field)" :key="'trl' + li">
                          <span class="design-element-meta-err" :title="line.err" v-if="line.err">✕</span>
                          <span class="design-element-meta-fact" v-for="(fact, fi) in line.facts" :key="'trf' + li + '-' + fi">
                            <span class="design-element-meta-eq" v-if="fact.eq">=</span>
                            <i :class="[fact.icon, 'design-element-meta-icon']" :title="fact.title" v-if="fact.icon"></i>
                            <b v-if="fact.strong">{{ fact.strong }}</b>
                            <template v-if="fact.text">{{ fact.text }}</template>
                            <fts-formula-display
                              :variables="variables"
                              :form="form"
                              :formula="fact.formula"
                              v-if="fact.formula"
                            />
                            <span class="design-element-meta-sep" v-if="fi < line.facts.length - 1">·</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <div class="design-element-ledger-body" v-if="expandedColumnId === field.id">
                      <fts-field-behavior-ledger
                        :form-data="field"
                        :variables="variables"
                        :form="form"
                        :actions="getActions(field)"
                        :information-formula="computedFormula(field) || informationFormula(field)"
                        :is-computed="isComputed(field)"
                        :is-parent-formula-mode="Boolean(item.formulaMode)"
                        :activity-id="activityId"
                        :parent-name="item.displayName"
                      >
                        <template #extra-rules>
                          <div class="formula-rule-group" v-for="row in extraTogglesFor(field)" :key="row.key">
                            <div class="formula-rule">
                              <div class="form-formula-label">
                                <i :class="[row.icon, 'form-formula-label-icon']"></i> {{ row.label }}
                              </div>
                              <div class="formula-rule-states">
                                <template v-for="(st, si) in row.states">
                                  <span class="formula-rule-sep" v-if="si" :key="row.key + 'sep' + si">·</span>
                                  <span
                                    class="formula-rule-state"
                                    :class="{ 'formula-rule-state--on': st.active }"
                                    @click="!st.active && row.toggle()"
                                    :key="row.key + 'st' + si"
                                  >{{ st.label }}</span>
                                </template>
                              </div>
                            </div>
                          </div>
                        </template>
                      </fts-field-behavior-ledger>
                      <div class="design-element-ledger-actions">
                        <span class="design-element-ledger-action" @click="selectMenuAction('configure', field, { parentId: item.id })">Configure</span>
                        <span class="design-element-ledger-action-sep">·</span>
                        <span class="design-element-ledger-action" @click="selectMenuAction('edit', field, { parentId: item.id })">Edit information</span>
                        <span class="design-element-ledger-action-sep">·</span>
                        <span class="design-element-ledger-action" @click="selectMenuAction('hideColumn', field)">Remove from table</span>
                        <span
                          class="design-element-ledger-action design-element-ledger-action--danger"
                          @click="selectMenuAction('delete', field, { parentId: item.id })"
                        >Delete information</span>
                      </div>
                    </div>
                  </div>
                  <fts-sync-chip
                    :el="field"
                    :stored="storedOnlyBadgeFor(field)"
                    column
                  />
                </div>
              </template>
            </draggable>
            <div class="design-element-table-foot">
              <span class="design-element-table-foot-hidden" v-if="invisibleFields.length">
                {{ invisibleFields.length }} hidden column{{ invisibleFields.length > 1 ? 's' : '' }} —
                <span
                  class="design-element-table-foot-name"
                  v-for="(hf, hi) in invisibleFields"
                  :key="hf.id"
                  @click="showColumn(hf)"
                  :title="'Show ' + (hf.label || hf.displayName)"
                >{{ (hi ? ' · ' : '') + (hf.label || hf.displayName) }}</span>
              </span>
              <fts-form-design-shortcut
                :modules="modules"
                :options="invisibleFields"
                :selected-options="visibleFields"
                :exclude-types="[14, 15]"
                suggestion
                placement="bottom-start"
                class="design-element-table-foot-add"
                @option:selected="showColumn"
                @option:create="(x) => createColumn(x, item)"
              >
                <span class="design-element-table-foot-add-label">＋ add column</span>
              </fts-form-design-shortcut>
            </div>
          </div>
          <draggable
            class="design-element-table-columns fts-scrollbar"
            :group="item.fieldName"
            :list="item.fields"
            v-bind="dragOptions"
            handle=".element-designer-item-action--drag-handle"
            draggable=".design-element-table-column"
            ref="designElementTableColumns"
            v-else
          >
            <template v-for="(field, fieldIndex) in item.fields">
              <div
                class="design-element-table-column"
                :key="field.id"
                v-if="!field.hide"
              >
                <div class="design-element-table-column-element">
                  <div class="element-designer-item-action-container" v-if="!preview">
                    <fts-form-design-shortcut
                      :modules="modules"
                      :options="invisibleFields"
                      :selected-options="visibleFields"
                      :exclude-types="[14, 15]"
                      suggestion
                      placement="bottom-start"
                      class="element-designer-item-action"
                      @option:selected="(x) => showColumn(x, fieldIndex + 1)"
                      @option:create="(x) => createColumn(x, item, fieldIndex + 1)"
                    >
                      <fts-tippy>
                        <font-awesome-icon :icon="['fas', 'plus']" fixed-width />
                        <div slot="content" class="tippy-hint">
                          <span class="tippy-hint-highlight">Click</span> to add a field
                        </div>
                      </fts-tippy>
                    </fts-form-design-shortcut>
                    <fts-dropdown-select
                      append-to-body
                      append-content-class="dropdown-menu"
                      class="element-designer-item-action element-designer-item-action--drag-handle"
                      @selected="(evt) => selectMenuAction(evt, field, { parentId: item.id})"
                    >
                      <fts-tippy slot="toggle">
                        <font-awesome-icon
                          :icon="['fas', 'grip-vertical']"
                          fixed-width
                        />
                        <template #content>
                          <div class="tippy-hint">
                            <span class="tippy-hint-highlight">Drag</span> to move
                          </div>
                          <div class="tippy-hint">
                            <span class="tippy-hint-highlight">Click</span> to open menu
                          </div>
                        </template>
                      </fts-tippy>
                      <template #menu="{ select }">
                        <div
                          class="dropdown-option"
                          @click="select('configure')"
                          v-if="
                            item.input !== 'empty' &&
                            item.input !== 'section' &&
                            item.input !== 'description' &&
                            item.input !== 'image' &&
                            !document
                          "
                        >
                          <div class="dropdown-option-icon">
                            <font-awesome-icon :icon="['fas', 'cog']" fixed-width />
                          </div>
                          <div>Configure</div>
                        </div>
                        <div class="dropdown-option" @click="select('hideColumn')">
                          <div class="dropdown-option-icon">
                            <font-awesome-icon :icon="['fas', 'times']" fixed-width />
                          </div>
                          <div>Remove</div>
                        </div>
                        <div class="dropdown-divider"></div>
                        <div class="dropdown-option" @click="select('edit')">
                          <div class="dropdown-option-icon">
                            <font-awesome-icon :icon="['far', 'edit']" fixed-width />
                          </div>
                          <div>Edit Information</div>
                        </div>
                        <div class="dropdown-option dropdown-option--danger" @click="select('delete')">
                          <div class="dropdown-option-icon">
                            <font-awesome-icon :icon="['far', 'trash-alt']" fixed-width />
                          </div>
                          <div>Delete Information</div>
                        </div>
                      </template>
                    </fts-dropdown-select>
                  </div>
                  <div class="design-element-table-column-form-element">
                    <!-- chip anchors to this column's top-right corner: the hover toolbar owns the
                         LEFT of the header, so anything beside the label gets covered by it (and
                         with it, the chip's tooltip) -->
                    <fts-sync-chip
                      :el="field"
                      :disabled="Boolean(document)"
                      :stored="storedOnlyBadgeFor(field)"
                      column
                    />
                    <div
                      class="design-element-ledger-head"
                      @click="!document && !preview && openFormulaModal(field, item)"
                    >
                      <fts-form-label
                        :icon="getIcon(field.type)"
                        class="design-element-form-label"
                        :class="{ 'design-element-form-label--invisible': field.visible === false}"
                      >
                        {{ field.label || field.displayName }}
                        <i
                          class="fts-icon-asterisk design-element-required-mark"
                          v-if="field.required || field.requiredFormula"
                        ></i>
                      </fts-form-label>
                      <div class="design-element-meta" v-if="!document && !preview && metaLinesFor(field).length">
                        <div class="design-element-meta-line" v-for="(line, li) in metaLinesFor(field)" :key="'cl' + li">
                          <span class="design-element-meta-err" :title="line.err" v-if="line.err">✕</span>
                          <span class="design-element-meta-fact" v-for="(fact, fi) in line.facts" :key="'cf' + li + '-' + fi">
                            <span class="design-element-meta-eq" v-if="fact.eq">=</span>
                            <i
                              :class="[fact.icon, 'design-element-meta-icon']"
                              :title="fact.title"
                              v-if="fact.icon"
                            ></i>
                            <b v-if="fact.strong">{{ fact.strong }}</b>
                            <template v-if="fact.text">{{ fact.text }}</template>
                            <fts-formula-display
                              :variables="variables"
                              :form="form"
                              :formula="fact.formula"
                              v-if="fact.formula"
                            />
                            <span class="design-element-meta-sep" v-if="fi < line.facts.length - 1">·</span>
                          </span>
                        </div>
                      </div>
                    </div>
                    <!-- shortcut: only the toggles the behavior modal doesn't cover -->
                    <div
                      class="design-element-shortcut"
                      v-if="!document && !preview && columnShortcutActions(field).length"
                    >
                      <div class="design-element-shortcut-gap">
                        <template v-for="action in columnShortcutActions(field)">
                          <template v-if="action === Required">
                            <fts-tippy :key="action.id" v-if="item.formulaMode || field.formulaMode">
                              <div>
                                <i class="fts-icon-asterisk design-element-shortcut-action design-element-shortcut-action--readonly"></i>
                              </div>
                              <div slot="content" class="tippy-hint fts-text-left">
                                <div class="tippy-hint-highlight">Information formula mode</div>
                                <div>Values can't be directly edited.</div>
                              </div>
                            </fts-tippy>
                            <fts-tippy
                              :key="action.id"
                              content="set field as required"
                              v-else
                            >
                              <div
                                class="design-element-shortcut-toggle"
                                @click="updateItem(field, 'required')"
                              >
                                <i
                                  class="fts-icon-asterisk design-element-shortcut-action"
                                  :class="{ 'design-element-shortcut-action--active': field.required }"
                                ></i>
                              </div>
                            </fts-tippy>
                          </template>
                          <template v-else-if="action === ReadOnly">
                            <fts-tippy :key="action.id" v-if="item.formulaMode || field.formulaMode">
                              <div>
                                <i class="fts-icon-pencil-slash design-element-shortcut-action design-element-shortcut-action--readonly"></i>
                              </div>
                              <div slot="content" class="tippy-hint fts-text-left">
                                <div class="tippy-hint-highlight">Information formula mode</div>
                                <div>Values can't be directly edited.</div>
                              </div>
                            </fts-tippy>
                            <fts-tippy
                              :key="action.id"
                              content="set field as view only"
                              v-else
                            >
                              <div
                                class="design-element-shortcut-toggle"
                                @click="updateItem(field, 'readOnly')"
                              >
                                <i
                                  class="fts-icon-pencil-slash design-element-shortcut-action"
                                  :class="{ 'design-element-shortcut-action--active': field.readOnly }"
                                ></i>
                              </div>
                            </fts-tippy>
                          </template>
                          <fts-tippy
                            :key="action.id"
                            content="set field as assign"
                            v-else-if="action === Assignee"
                          >
                            <div
                              class="design-element-shortcut-toggle"
                              @click="updateItem(field, 'assignee')"
                            >
                              <i
                                class="fts-icon-user-check design-element-shortcut-action"
                                :class="{ 'design-element-shortcut-action--active': field.assignee }"
                              ></i>
                            </div>
                          </fts-tippy>
                          <fts-tippy
                            :key="action.id"
                            content="set field as due"
                            v-else-if="action === Due"
                          >
                            <div
                              class="design-element-shortcut-toggle"
                              @click="updateItem(field, 'due')"
                            >
                              <i
                                class="fts-icon-calendar-check design-element-shortcut-action"
                                :class="{ 'design-element-shortcut-action--active': field.due }"
                              ></i>
                            </div>
                          </fts-tippy>
                          <fts-tippy
                            :key="action.id"
                            content="set field as addable"
                            v-else-if="action === Addable"
                          >
                            <div
                              class="design-element-shortcut-toggle"
                              @click="updateItem(field, 'disabledAddRemoveItems')"
                            >
                              <i
                                class="fts-icon-add-remove design-element-shortcut-action"
                                :class="{ 'design-element-shortcut-action--active': !field.disabledAddRemoveItems }"
                              ></i>
                            </div>
                          </fts-tippy>
                          <fts-tippy
                            :key="action.id"
                            content="set field visibility"
                            v-else-if="action === Visible"
                          >
                            <div
                              class="design-element-shortcut-toggle"
                              @click="updateVisible(field, 'visible')"
                            >
                              <i
                                class="fts-icon-hidden design-element-shortcut-action"
                                :class="{ 'design-element-shortcut-action--active': field.visible === false }"
                              ></i>
                            </div>
                          </fts-tippy>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <fts-form-design-shortcut
              slot="footer"
              :modules="modules"
              :options="invisibleFields"
              :selected-options="visibleFields"
              :exclude-types="[14, 15]"
              suggestion
              placement="bottom-start"
              class="design-element-table-columm-create"
              v-if="!preview"
              @option:selected="showColumn"
              @option:create="(x) => createColumn(x, item)"
            >
              <fts-tippy appendTriggerClass="design-element-table-columm-create-toggle">
                <font-awesome-icon
                  :icon="['fas', 'plus']"
                />
                <div slot="content" class="tippy-hint">
                  <span class="tippy-hint-highlight">Click</span> to add a field
                </div>
              </fts-tippy>
            </fts-form-design-shortcut>
          </draggable>
        </template>
        <!-- information:list -->
        <template v-else-if="item.type == 15">
          <fts-form-layouter
            :modules="modules"
            :activity-design="form"
            :activity-id="activityId"
            :form="item.design"
            :item="item"
            :item-group="item.fieldName"
            :form-data="{}"
            :shortcut-options="listInformation"
            :shortcut-selected-options="listClassificationInformation"
            :document="document"
            :preview="preview"
            :preview-data="previewData"
            v-if="item.design"
          />
        </template>
      </div>
      <!-- calm dialect for sections & content elements (ruling 2026-08-11): the kind named as
           quiet ledger text with the same meta line and behavior ledger every field row has;
           the legacy hover toolbar and tag stack retire -->
      <template v-if="!document && !preview && !isLedgerHost">
        <div class="design-element-kindbar" @click="expanded = !expanded">
          <span class="design-element-kindbar-kind">{{ kindLabel }}</span>
          <div class="design-element-meta" v-if="metaLines.length">
            <div class="design-element-meta-line" v-for="(line, li) in metaLines" :key="'kl' + li">
              <span class="design-element-meta-err" :title="line.err" v-if="line.err">✕</span>
              <span class="design-element-meta-fact" v-for="(fact, fi) in line.facts" :key="'kf' + li + '-' + fi">
                <span class="design-element-meta-eq" v-if="fact.eq">=</span>
                <i :class="[fact.icon, 'design-element-meta-icon']" :title="fact.title" v-if="fact.icon"></i>
                <b v-if="fact.strong">{{ fact.strong }}</b>
                <template v-if="fact.text">{{ fact.text }}</template>
                <fts-formula-display
                  :variables="variables"
                  :form="form"
                  :formula="fact.formula"
                  v-if="fact.formula"
                />
                <span class="design-element-meta-sep" v-if="fi < line.facts.length - 1">·</span>
              </span>
            </div>
          </div>
        </div>
        <div class="design-element-ledger-body" v-if="expanded">
          <fts-field-behavior-ledger
            :form-data="item"
            :variables="variables"
            :form="form"
            :actions="getActions(item)"
            :activity-id="activityId"
          />
          <div class="design-element-ledger-actions">
            <span class="design-element-ledger-action" @click="selectMenuAction('remove', item)">Remove from form</span>
          </div>
        </div>
      </template>
    </div>
    <!-- form formula modal -->
    <fts-form-formula-modal ref="ftsFormFormulaModal" />
    <!-- information design modal -->
    <fts-information-design-modal ref="ftsInformationDesignModal" />
    <!-- information delete confirmation -->
    <fts-information-delete-confirmation ref="ftsInformationDeleteConfirmation" />
    <!-- fts-information-modal -->
    <fts-control-modal
      @ok="confirm($event)"
      :modalNameId="id"
    />
  </div>
</template>
<script>
import Vue from "vue";
import { store as canvasDesignStore} from '../store/canvasDesignStore';
import { authorizationStore } from '../store/authorizationStore';
import commons from '../commons/commons';
import canvasDesign from '../commons/canvasDesign';
import formDesign from '../commons/formDesign';

import draggable from 'vuedraggable';
import wysiwyg from 'vue-wysiwyg';
Vue.use(wysiwyg, {});

import FTSBtn from './FTSBtn';
import FTSContenteditable from './FTSContenteditable';
import FTSDropdownSelect from './FTSDropdownSelect';
import FTSFileUpload from './FTSFileUpload';
import FTSFormDesigner from './FTSFormDesigner';
import FTSFormLabel from './FTSFormLabel';
import FTSFormElement from './FTSFormElement';
import FTSTextArea from './FTSTextArea';
import FTSTippy from './FTSTippy';
import FTSToggleButton from './FTSToggleButton';
import FTSToggleSwitch from './FTSToggleSwitch';
import FTSFormEditor from './form/FTSFormEditor';
import FTSControlModal from '../design/FTSControlModal';
import FTSFormDesignShortcut from '../design/FTSFormDesignShortcut';
import FTSFieldBehaviorLedger from '../design/FTSFieldBehaviorLedger';
import { diagnose } from '../design/formula/formulaDiagnostics';
import { loadFormulaFunctions } from '../design/formula/formulaFunctions';
import FTSFormula from '../design/FTSFormula';
import FTSFormulaDisplay from '../design/FTSFormulaDisplay';
import FTSFormFormulaModal from '../design/FTSFormFormulaModal';
import FTSInformationModal from '../design/FTSInformationModal';
import FTSInformationDesignModal from '../design/FTSInformationDesignModal';
import FTSInformationDeleteConfirmation from '../design/FTSInformationDeleteConfirmation';
import FTSSyncChip from './FTSSyncChip';

export default {
  components: {
    draggable,
    "fts-btn": FTSBtn,
    "fts-contenteditable": FTSContenteditable,
    "fts-dropdown-select": FTSDropdownSelect,
    "fts-file-upload": FTSFileUpload,
    "fts-form-designer": FTSFormDesigner,
    "fts-form-label": FTSFormLabel,
    "fts-form-element": FTSFormElement,
    "fts-text-area": FTSTextArea,
    "fts-tippy": FTSTippy,
    "fts-toggle-button": FTSToggleButton,
    "fts-toggle-switch": FTSToggleSwitch,
    "fts-form-editor": FTSFormEditor,
    "fts-control-modal": FTSControlModal,
    "fts-form-design-shortcut": FTSFormDesignShortcut,
    "fts-field-behavior-ledger": FTSFieldBehaviorLedger,
    "fts-formula": FTSFormula,
    "fts-formula-display": FTSFormulaDisplay,
    'fts-form-formula-modal': FTSFormFormulaModal,
    "fts-information-modal": FTSInformationModal,
    "fts-information-design-modal": FTSInformationDesignModal,
    "fts-information-delete-confirmation": FTSInformationDeleteConfirmation,
    "fts-sync-chip": FTSSyncChip
  },
  data() {
    return {
      id: null,
      formData: {},
      dragOptions: commons.dragOptions,
      /* design-mode row disclosure (§3.2b: the row is the disclosure) */
      expanded: false,
      /* table columns: which column's inline ledger is open (one at a time) */
      expandedColumnId: null,
      /* function registry for at-rest formula health marks (session-cached load) */
      fnRegistry: null
    };
  },
  props: [
    "elParentId",
    "modules",
    "item",
    "document",
    "activityId",
    "form",
    "shortcutOptions",
    "shortcutElements",
    "shortcutSelectedOptions",
    "preview",
    "previewData"
  ],
  watch: {
    "item.type": function () {
      if (this.item.type == "tab") {
        if (this.item.tabs == null) {
          Vue.set(this.item, "tabs", [
            {
              id: commons.newUUID(),
              header: "(new tab)",
              design: { rows: [{ index: commons.newUUID(), items: [] }] },
            },
          ]);
        }
      }
    },
  },
  methods: {
    getTabs(tabs) {
      if (!tabs.find((x) => x.selected) && tabs && tabs.length) {
        Vue.set(tabs[0], "selected", true);
      }
      return tabs;
    },
    addTab(index) {
      this.item.tabs.splice(index || 0, 0 , {
        id: commons.newUUID(),
        header: "(new tab)",
        design: { rows: [] }
      });
    },
    removeTab(tab) {
      let ind = this.item.tabs.indexOf(tab);
      this.item.tabs.splice(ind, 1);
    },
    elementAdded(element) {
      this.$emit("added", element);
    },
    async remove() {
      const isFormElement = formDesign.isFormElement(this.item.input);
      if (isFormElement) {
        this.$emit("remove", this.item);
      } else {
        let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
        const forms = formDesign.getFormsByInformationId(
          canvas,
          this.item.id,
          { excluded: [this.activityId]}
        );
        if (forms && forms.length) {
          this.$emit("remove", this.item);
        } else {
          const res = await this.$refs.ftsInformationDeleteConfirmation.show({
            canvas: canvas,
            information: this.item,
            forms: forms,
            type: 'remove'
          });
          if (res === 'remove') {
            this.$emit("remove", this.item);
          } else if (res === true) {
            this.deleteInformation(canvas, this.item);
          }
        }
      }
    }, 
    getIcon(typeId) {
      return commons.informationIcon(typeId);
    },
    /* shared by field rows and table columns: the calm meta as LINES (ruling 2026-08-10) —
       every formula-bearing fact gets its own separately-ellipsized line: the value formula
       first (just the "=", no icon), each conditional rule with its icon, and the short text
       facts sharing one final line. A formula the save-time lint would reject wears the ✕
       mark (form health, at rest). */
    metaLinesFor(item) {
      const lines = [];
      const line = (facts, formulaText) => lines.push({
        facts,
        err: formulaText ? this.firstFormulaError(formulaText) : null
      });
      const compF = this.computedFormula(item);
      const infoF = !compF && this.informationFormula(item);
      if (compF) {
        line([{ text: 'Computed' }, { eq: true, formula: compF }], compF);
      } else if (infoF) {
        line([{ text: 'Information formula' }, { eq: true, formula: infoF }], infoF);
      } else if (item.formula) {
        line([{ eq: true, formula: item.formula }], item.formula);
      }
      if (item.requiredFormula) {
        line([{ icon: 'fts-icon-asterisk', title: 'Required when', formula: item.requiredFormula }], item.requiredFormula);
      }
      if (!(compF || item.readOnly) && item.readOnlyFormula) {
        line([{ icon: 'fts-icon-pencil-slash', title: 'View only when', formula: item.readOnlyFormula }], item.readOnlyFormula);
      }
      if (item.visible !== false && item.visibleFormula) {
        line([{ icon: 'fts-icon-hidden', title: 'Visible when', formula: item.visibleFormula }], item.visibleFormula);
      }
      const shorts = [];
      if (compF || item.readOnly) shorts.push({ icon: 'fts-icon-pencil-slash', title: 'View only' });
      if (item.visible === false) shorts.push({ icon: 'fts-icon-hidden', title: 'Hidden' });
      if (item.assignee) shorts.push({ text: 'assignee' });
      if (item.unassignee) shorts.push({ text: 'removes assignee' });
      if (item.addAssignee) shorts.push({ text: 'adds assignee' });
      if (item.due) shorts.push({ text: 'due' });
      if (item.disabledAddRemoveItems) shorts.push({ text: 'add/remove locked' });
      if (item.queryScript) shorts.push({ text: 'query script' });
      if (this.isQueryFilter(item.filter)) shorts.push({ text: 'filter' });
      if (shorts.length) line(shorts, null);
      return lines;
    },
    /* first save-blocking problem in a serialized formula (chips as {{…}}), or null. Runs
       the same diagnose the editors run live; registry loads once per session. */
    firstFormulaError(formulaText) {
      if (!this.fnRegistry || !formulaText) return null;
      const errors = diagnose(formulaText, this.fnRegistry, { excelSyntax: true })
        .filter(d => d.severity === 'error');
      return errors.length ? errors[0].message : null;
    },
    /* table columns: only the toggles the behavior modal does NOT cover — the header click
       opens the modal for required / view only / visible / formulas */
    columnShortcutActions(field) {
      return this.getActions(field).filter(a =>
        [this.Assignee, this.Unassignee, this.AddAssignee, this.Due, this.Addable].indexOf(a) > -1
      );
    },
    toggleColumn(field) {
      this.expandedColumnId = this.expandedColumnId === field.id ? null : field.id;
    },
    /* the toolbar's rarely-used cousins as two-state ledger rows (leftmost = default) —
       shared by the row expansion (this.item) and each table column */
    extraTogglesFor(item) {
      const actions = this.getActions(item);
      const locked = Boolean(item.formulaMode);
      const rows = [];
      const push = (key, label, icon, on, toggle, defaultOn) => {
        const states = [
          { label: defaultOn ? 'On' : 'Off', active: defaultOn ? on : !on },
          { label: defaultOn ? 'Off' : 'On', active: defaultOn ? !on : on }
        ];
        rows.push({ key, label, icon, states, toggle });
      };
      if (actions.indexOf(this.Assignee) > -1) {
        push('assignee', 'Assignee', 'fts-icon-user-check',
          Boolean(item.assignee), () => this.updateItem(item, 'assignee'), false);
      }
      if (actions.indexOf(this.Unassignee) > -1 && this.beta) {
        push('unassignee', 'Remove assignee', 'fts-icon-user',
          Boolean(item.unassignee), () => this.updateItem(item, 'unassignee'), false);
      }
      if (actions.indexOf(this.AddAssignee) > -1 && this.isInternal) {
        push('addAssignee', 'Add assignee', 'fts-icon-user-plus',
          Boolean(item.addAssignee), () => this.updateItem(item, 'addAssignee'), false);
      }
      if (actions.indexOf(this.Due) > -1) {
        push('due', 'Due date', 'fts-icon-calendar-check',
          Boolean(item.due), () => this.updateItem(item, 'due'), false);
      }
      if (actions.indexOf(this.Addable) > -1 && !locked) {
        push('addable', 'Add / remove entries', 'fts-icon-add-remove',
          !item.disabledAddRemoveItems, () => this.updateItem(item, 'disabledAddRemoveItems'), true);
      }
      return rows;
    },
    hideColumn(column) {
      this.$set(column, "hide", true);
    },
    showColumn(item, index) {
      let column = this.item.fields.find((x) => x.id === item.id);
      if (index) {
        this.item.fields.splice(this.item.fields.indexOf(column), 1);
        this.item.fields.splice(index, 0, column);
      }
      column && this.$delete(column, "hide");
    },
    async createColumn(item, parent, index) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      const res = await this.$refs.ftsInformationDesignModal.show(
        canvasDesign.initInformation(item),
        null, null, { parentId: parent.id }
      );
      if (res && res !== true) {
        canvasDesign.setInformation(canvas, _.cloneDeep(res), parent.id);
        canvasDesignStore.dispatch("updateVectorCanvas", canvas);
        if (!Array.isArray(parent.fields)) {
          this.$set(parent, "fields", [res]);
        } else if (index) {
          parent.fields.splice(index, 0 , res);
        } else {
          parent.fields.push(res);
        }
      }
    },
    configure(item) {
      let formData = item;
      let form = this.form;
      this.$modal.show("controlModal" + this.id, { formData, form });
    },
    updateItem(item, arg) {
      if (
        item[arg] === null ||
        item[arg] === undefined ||
        item[arg] === false
      ) {
        this.$set(item, arg, true);
        if (arg === this.Assignee) {
          this.$delete(item, this.Unassignee);
          this.$delete(item, this.AddAssignee);
        } else if (arg === this.Unassignee) {
          this.$delete(item, this.Assignee);
          this.$delete(item, this.AddAssignee);
        } else if (arg === this.AddAssignee) {
          this.$delete(item, this.Assignee);
          this.$delete(item, this.Unassignee);
        }
      } else {
        this.$delete(item, arg);
      }
    },
    updateVisible(item, arg) {
      if (item[arg] === false) {
        this.$delete(item, arg);
      } else {
        this.$set(item, arg, false);
      }
    },
    confirm(formData) {
      /* update table columns */
      if (this.item.type === 14 && this.item.fields) {
        let column = this.item.fields.find((x) => x.id === formData.id);
        if (column) {
          column = formData;
          return;
        }
      }
      /* update information */
      this.item = formData;
    },
    isFormulaScript(item) {
      return Boolean(
        item.formula ||
        item.requiredFormula ||
        item.readOnlyFormula ||
        item.visibleFormula
      );
    },
    informationFormula(item) {
      if (item.formulaMode) {
        try {
          const res = _.findDeep(this.information, (x) => x.id == item.id, {
            childrenPath: 'fields'
          });
          return res && res.value && res.value.formula;
        } catch(err) { console.error(err); }
      }
    },
    /* The computed (formula) Information field this form element/column renders, resolved by id so it
       is robust regardless of whether `computed`/`formula` were propagated onto the design element.
       A computed field's formula is owned by the Information definition -- it cannot be edited from
       the form and is always read-only -- so the form design only indicates it. */
    computedInformation(item) {
      if (!item || !item.id) return null;
      try {
        const res = _.findDeep(this.information, (x) => x.id == item.id, { childrenPath: 'fields' });
        return res && res.value && res.value.computed ? res.value : null;
      } catch (err) { console.error(err); return null; }
    },
    isComputed(item) {
      return !!this.computedInformation(item);
    },
    computedFormula(item) {
      const info = this.computedInformation(item);
      return info && info.formula;
    },
    /* Notice for a COMPUTED field (or table column) whose formula reads fields that are NOT on
       this form: the form shows the stored value only and cannot recompute it while editing —
       the server recomputes when the entry is saved. Pointless on view forms (nothing edits
       there), and resolved against the working design so it reacts as fields are added/removed. */
    storedOnlyBadgeFor(el) {
      if (this.document || !el || !el.id) return null;
      if (this.activityId === "view" || this.activityId === "quickView") return null;
      const info = this.computedInformation(el);
      if (!info || !info.formula) return null;
      const missing = formDesign.computedFormulaMissingDependencies(
        this.form, this.information, info.formula
      );
      if (!missing.length) return null;
      const names = missing.map(x => x.displayName || x.fieldName).join(", ");
      return {
        label: "stored",
        tip: `Shows the stored value only (blank for a new entry) — ${names} `
          + `${missing.length > 1 ? "are" : "is"} not on this form, so this computed field `
          + `won't recalculate while editing. It recalculates on the server when the entry is saved.`
      };
    },
    async openFormulaModal(data, parent) {
      const res = await this.$refs.ftsFormFormulaModal.show({
        formData: data,
        variables: this.variables,
        form: this.form,
        actions: this.getActions(data),
        // a computed field's formula is owned by Information -> show it read-only; otherwise the
        // field's own information-formula-mode value (if any).
        informationFormula: this.computedFormula(data) || this.informationFormula(data),
        isComputed: this.isComputed(data),
        isParentFormulaMode: Boolean(parent && parent.formulaMode),
        // engine-badge context: `parent` is the table item when editing a column's formulas
        activityId: this.activityId,
        parentName: parent && Array.isArray(parent.fields) ? parent.displayName : null
      });
      if (res) {
        if (parent && parent.fields) {
          const index = parent.fields.findIndex((x) => x.id == data.id);
          if (index > -1) {
            this.$set(parent.fields, index, res);
          }
        } else {
          this.$emit('update:item', res);
        }
      }
    },
    getActions(item) {
      if (item.input == "description" || item.input == "image" || item.input == "empty") {
        return [this.Visible];
      } else if (item.input == "button") {
        return [this.ReadOnly, this.Visible];
      } else if (
        item.type == 5 ||
        item.type == 6 ||
        item.type == 30 ||
        item.type == 31
      ) {
        return [this.Required, this.ReadOnly, this.Due, this.Visible, this.Formula];
      } else if (item.type == 14 || item.type == 15) {
        return [this.Required, this.ReadOnly, this.Addable, this.Visible, this.Formula];
      } else if (item.type == 20 || item.type == 21) {
        return [this.Required, this.ReadOnly, this.Assignee, this.Unassignee, this.AddAssignee, this.Visible, this.Formula];
      } else if (item.input == "section") {
        return [this.Visible];
      } else {
        return [this.Required,this.ReadOnly, this.Visible, this.Formula];
      }
    },
    isFormulaAction(actions) {
      return [this.Formula, this.Required, this.ReadOnly, this.Visible].some(x => actions.indexOf(x) > -1);
    },
    isQueryFilter(filter) {
      return filter && Array.isArray(filter.items) ? Boolean(filter.items.length) : false;
    },
    selectMenuAction(action, item, payload = {}) {
      if (action === 'configure') {
        this.configure(item);
      } else if (action === 'edit') {
        this.openInformationDesignModal(item, payload.parentId);
      } else if (action === 'delete') {
        this.openInformationDeleteConfirmation(item);
      } else if (action === 'remove') {
        this.remove();
      } else if (action === 'hideColumn') {
        this.hideColumn(item);
      }
    },
    deleteInformation(canvas, item) {
      canvasDesign.deleteInformation(canvas, item.id);
      canvasDesignStore.dispatch("updateVectorCanvas", canvas);
      if (this.item.type == 14 && this.item.id !== item.id) {
        commons.expandWorkDesignInternal(this.information, this.item);
      } else {
        this.$emit("remove", this.item);
      }
    },
    async openInformationDeleteConfirmation(item) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      const res = await this.$refs.ftsInformationDeleteConfirmation.show({
        canvas: canvas,
        information: item,
        payload: { excluded: [this.activityId]}
      });
      if (res === true) {
        this.deleteInformation(canvas, item);
      }
    },
    async openInformationDesignModal(item, parentId) {
      let canvas = _.cloneDeep(canvasDesignStore.state.canvas);
      const information = canvasDesign.getInformation(canvas, item.id);
      if (information) {
        const res = await this.$refs.ftsInformationDesignModal.show(
          {...canvasDesign.initInformation(information)},
          /* forward the table id when editing a field inside a table/list so the modal enters
             column (within-row) context — needed for a brand-new column not yet in the canvas tree,
             where the modal can't infer the parent from the field's position alone. */
          null, null, { activityId: this.activityId, parentId: parentId || null }
        );
        if (res && res !== true) {
          canvasDesign.setInformation(canvas, res, parentId);
          canvasDesignStore.dispatch("updateVectorCanvas", canvas);
          if (parentId) {
            if (this.item.type == 14) {
              const parent = canvasDesign.getInformation(canvas, parentId);
              if (parent && Array.isArray(parent.fields)) {
                commons.expandWorkDesignInternal(parent.fields, item);
              }
            }
          } else {
            // to-do update list field
            commons.expandWorkDesignInternal(this.information, item);
            this.$emit("update:item", item);
          }
        } else if (res === true) {
          this.deleteInformation(canvas, information);
        }
      } else {
        this.$toasted.global.error(`Something went wrong. Information #${item.displayName} not found.`);
      }
    }
  },
  computed: {
    /* default-branch field items own the ledger row; sections and custom content elements
       (description/image/empty/button) keep the legacy hover shortcut + tags */
    isLedgerHost() {
      return !['section', 'description', 'image', 'empty', 'button'].includes(this.item.input);
    },
    isInformationItem() {
      return !['empty', 'section', 'description', 'image', 'button', 'custom'].includes(this.item.input);
    },
    /* preview controls query with the runtime lens of the combined standard activity */
    previewActivityId() {
      return this.activityId === 'createEditView' ? 'create' : this.activityId;
    },
    /* the kind bar names what a content element IS — quiet ledger text, click to open its
       behavior ledger */
    kindLabel() {
      if (this.item.input === 'section') {
        return this.item.type ? `section · ${this.item.type}` : 'section';
      }
      if (this.item.input === 'empty') return 'empty space';
      return this.item.input;
    },
    /* the rest-state meta line: every fact about the field, calm — the value formula speaks
       as "= expr" (no "Formula" header), rule facts speak as their icons (bare icon = always,
       icon + expr = conditional; static required lives on the label's mark instead). A field
       with no story has no line. */
    metaLines() {
      return this.metaLinesFor(this.item);
    },
    /* the toolbar's rarely-used cousins for the ROW expansion — see extraTogglesFor */
    extraToggles() {
      return this.extraTogglesFor(this.item);
    },
    variables() {
      return [
        {
          name: "Form",
          fieldName: "form",
          type: 35,
          moduleId: this.$store.state.vectorId.toString(),
          activityId: this.activityId,
          form: this.form,
        },
        {
          name: 'Entry',
          fieldName: 'entry',
          type: 7,
          moduleId: this.$store.state.vectorId.toString(),
        }
      ];
    },
    information() {
      return this.$store.state.canvas.information;
    },
    vectorId() {
      return this.$store.state.vectorId;
    },
    invisibleFields() {
      return this.item.fields.filter(x => x.hide);
    },
    visibleFields() {
      return this.item.fields.filter(x => !x.hide);
    },
    listClassificationInformation() {
      return commons.classificationInformation(this.item.design.rows, this.item.fields);
    },
    listInformation() {
      return this.item.fields.filter(x =>
        !this.listClassificationInformation.some(y => y.id === x.id)
      );
    },
    isInternal() {
      return authorizationStore.getters.isInternal;
    },
    beta() {
      return authorizationStore.getters.isBeta || this.$store.getters.workspaceBeta;
    },
  },
  created() {
    this.Formula = 'formula';
    this.Required = 'required';
    this.ReadOnly = 'readOnly';
    this.Visible = 'visible';
    this.Assignee = 'assignee';
    this.Unassignee = 'unassignee';
    this.AddAssignee = 'addAssignee';
    this.Due = 'due';
    this.Addable = 'addable';
  },
  mounted() {
    this.id = this._uid;
    // form-health marks: same registry the editors use, session-cached so this is instant
    // after the first load anywhere in the studio
    loadFormulaFunctions(action => canvasDesignStore.dispatch(action))
      .then(list => { this.fnRegistry = list; })
      .catch(() => { /* marks simply stay off */ });
  }
};
</script>